import React, { useEffect, useMemo, useRef } from 'react'
import { useWidgetContext } from '../../context/WidgetContext'
import { useChatContext } from '../../context/ChatContext'
import { MarkdownRenderer } from '../Markdown/MarkdownRenderer'
import { ThinkingBubble } from '../Markdown/ThinkingBubble'

const DEFAULT_KB_TITLE = 'Knowledge Base Article'

const prettyTitleFromUrl = (url?: string): string => {
  if (!url || typeof url !== 'string') return DEFAULT_KB_TITLE
  try {
    const u = new URL(url)
    const segments = u.pathname.split('/').filter(Boolean)
    if (!segments.length) return DEFAULT_KB_TITLE

    let last = segments[segments.length - 1]
    let base = last.replace(/\.[^/.]+$/, '')
    base = decodeURIComponent(base)
    base = base.replace(/[-_]+/g, ' ')
    base = base.replace(/[^a-zA-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim()

    if (!base) return DEFAULT_KB_TITLE

    return base
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  } catch {
    return DEFAULT_KB_TITLE
  }
}

const getKbUrl = (source: any): string | undefined => {
  const url =
    source?.url ||
    source?.webLocation?.url ||
    source?.location?.webLocation?.url ||
    source?.location?.url ||
    source?.metadata?.['x-amz-bedrock-kb-source-uri']

  return typeof url === 'string' && url.trim() ? url.trim() : undefined
}

const deriveKbTitle = (source: any): string => {
  const metaTitle =
    source?.title ||
    source?.metadata?.title ||
    source?.metadata?.fileName ||
    source?.metadata?.filename ||
    source?.metadata?.name

  if (typeof metaTitle === 'string' && metaTitle.trim()) return metaTitle.trim()
  return prettyTitleFromUrl(getKbUrl(source))
}

export const MessageList = ({
  listRef,
  showError,
}: {
  listRef: React.RefObject<HTMLDivElement | null>
  showError: boolean
}) => {
  const { config } = useWidgetContext()
  const { messages, isThinking, thinkingTime } = useChatContext()

  const lastAssistantElRef = useRef<HTMLDivElement | null>(null)

  // We "lock" scrollTop while streaming so the assistant text cascades downward
  const lockScrollTopRef = useRef<number | null>(null)
  const lockKeyRef = useRef<string>('')

  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (!messages[i]?.isUser) return i
    }
    return -1
  }, [messages])

  const lastAssistantKey = useMemo(() => {
    if (lastAssistantIndex < 0) return ''
    const m: any = messages[lastAssistantIndex]
    // prefer stable id if you have it
    return String(m?.id ?? `idx:${lastAssistantIndex}`)
  }, [messages, lastAssistantIndex])

  // When a NEW assistant message begins, scroll to its TOP and start locking
  useEffect(() => {
    const container = listRef.current
    const el = lastAssistantElRef.current
    if (!container || !el) return
    if (!lastAssistantKey) return

    // only when assistant "identity" changes
    if (lockKeyRef.current === lastAssistantKey) return

    lockKeyRef.current = lastAssistantKey

    // scroll so top of assistant message is visible
    const containerRect = container.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const offset = 12

    const targetScrollTop = container.scrollTop + (elRect.top - containerRect.top) - offset
    container.scrollTop = targetScrollTop

    // start locking at that position
    lockScrollTopRef.current = targetScrollTop
  }, [lastAssistantKey, listRef])

  useEffect(() => {
    const container = listRef.current
    if (!container) return

    if (isThinking && lockScrollTopRef.current != null) {
      const id = window.setInterval(() => {
        if (lockScrollTopRef.current != null) {
          container.scrollTop = lockScrollTopRef.current
        }
      }, 50)

      return () => window.clearInterval(id)
    }

    // when done, stop locking
    lockScrollTopRef.current = null
    return undefined
  }, [isThinking, listRef])

  return (
    <div ref={listRef} className="message-list">
      {messages.map((message, key) => {
        const isUser = message.isUser
        const rawMetadata = message.metadata
        const metadata = (() => {
          if (typeof rawMetadata !== 'string') return rawMetadata
          try {
            return JSON.parse(rawMetadata)
          } catch {
            return rawMetadata
          }
        })()
        const kbSources = !isUser ? metadata?.kbSources || [] : []
        const ragSources = !isUser
          ? metadata?.ragSources ||
            metadata?.rag_sources ||
            metadata?.siteCrawlSources ||
            metadata?.site_crawl_sources ||
            []
          : []
        const combinedSources = !isUser
          ? [
              ...(Array.isArray(kbSources) ? kbSources : []),
              ...(Array.isArray(ragSources) ? ragSources : []),
            ]
          : []
        const isLastAssistant = !isUser && key === lastAssistantIndex

        const normalizedKbSources = !isUser
          ? Array.from(
            new Map(
              combinedSources
                .map((s: any) => {
                  const url = getKbUrl(s)
                  return url ? [url, { ...s, url }] : null
                })
                .filter(Boolean) as any
            ).values()
          )
          : []

        return (
          <div
            key={key}
            ref={isLastAssistant ? lastAssistantElRef : undefined}
            className={`message-container ${isUser ? 'is-user' : 'is-assistant'}`}
          >
            {isLastAssistant && !isThinking && thinkingTime > 0 && (
              <ThinkingBubble show={true} elapsed={thinkingTime} done={true} />
            )}

            <div
              className={`message-content ${isUser ? 'user-message' : 'assistant-message'
                }`}
            >
              <MarkdownRenderer rawText={message.content} />
              {!isUser && metadata && config.mobileMcpToolBridge?.renderRecommendations?.(metadata)}

              {!isUser && normalizedKbSources.length > 0 && (
                <div className="kb-section d-block mt-5">
                  <h4>Referenced Articles:</h4>
                  <ul className="kb-list-reset mt-3">
                    {normalizedKbSources.map((s: any, idx: number) => (
                      <li key={idx} className="kb-list-item">
                        <i className="bi bi-file-text kb-link-icon" aria-hidden="true" />
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-underline"
                        >
                          {deriveKbTitle(s)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      })}

      {isThinking && (
        <ThinkingBubble show={true} elapsed={thinkingTime ?? 0} done={false} />
      )}

      {showError && (
        <div className="error-box">
          <p>An unexpected error occurred. Please try again later</p>
        </div>
      )}
    </div>
  )
}
